import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

function FeaturedCard({ item }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play().catch(() => {}); setPlaying(true); }
  };

  const badge = item.is_mix ? "MIX" : item.kind === "music" ? "MUSIC" : item.kind === "speech" ? "VOICE" : "SFX";

  return (
    <div data-testid="featured-card" className="group rounded-xl border border-white/10 bg-[color:var(--surface)] p-5 hover:border-white/25 transition-colors">
      <audio ref={audioRef} src={`${API}/share/${item.share_id}/audio`} preload="none" onEnded={() => setPlaying(false)} />
      <div className="flex items-center gap-3">
        <button
          data-testid="featured-play-btn"
          onClick={toggle}
          className="shrink-0 w-11 h-11 rounded-full bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] grid place-items-center transition-colors"
        >
          {playing ? <Pause size={17} weight="fill" color="white" /> : <Play size={17} weight="fill" color="white" />}
        </button>
        <div className="min-w-0">
          <p className="text-sm text-white/90 line-clamp-2 leading-snug">{item.prompt}</p>
          <div className="flex items-center gap-2 mt-1 text-[10px] font-mono-num uppercase tracking-widest text-white/40">
            <span className="px-1.5 py-0.5 rounded-sm bg-[color:var(--accent)]/20 text-[color:var(--accent)]">{badge}</span>
            {item.duration_seconds && <span>{Number(item.duration_seconds).toFixed(1)}s</span>}
          </div>
        </div>
      </div>
      <div className={`mt-3 flex items-end gap-[2px] h-6 ${playing ? "wave-playing" : ""}`}>
        {Array.from({ length: 36 }).map((_, i) => (
          <span key={i} className="wave-bar w-[3px] bg-[color:var(--accent)] opacity-60 rounded-full"
            style={{ height: `${20 + ((i * 7) % 80)}%`, animationDelay: `${(i * 60) % 800}ms` }} />
        ))}
      </div>
    </div>
  );
}

export default function FeaturedSounds() {
  const { t } = useLang();
  const [items, setItems] = useState([]);

  useEffect(() => {
    fetch(`${API}/featured`).then((r) => r.json()).then(setItems).catch(() => {});
  }, []);

  if (items.length === 0) return null;

  return (
    <section className="relative z-10 max-w-[1400px] mx-auto px-8 pb-28" data-testid="featured-section">
      <p className="text-xs font-mono-num uppercase tracking-widest text-white/40 mb-3">{t("featKicker")}</p>
      <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mb-10">{t("featTitle")}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((it) => <FeaturedCard key={it.share_id} item={it} />)}
      </div>
    </section>
  );
}
